import {
  getEndpointUrl,
  getStackType,
  hasDeployEnvironment,
  hasDeployStack,
  selectAppById,
  selectDatabaseById,
  selectEndpointById,
  selectEnvironmentById,
  selectStackById,
} from "@app/deploy";
import { selectOrganizationSelected } from "@app/organizations";
import { useSelector } from "@app/react";
import {
  appDetailUrl,
  databaseDetailUrl,
  endpointDetailUrl,
  environmentAppsUrl,
  stackDetailEnvsUrl,
} from "@app/routes";
import {
  getResourceStatId,
  selectResourcesByLastAccessedInCurrentOrg,
} from "@app/search";
import { capitalize } from "@app/string-utils";
import { Link } from "react-router-dom";
import { getStackImg } from "../stack";
import { Td, Tr } from "../table";
import { tokens } from "../tokens";
import {
  HOME_DASH_EVENTS,
  type HomeDashOrg,
  homeDashEvent,
  homeDashOrgPayload,
} from "./tuna-events";

const RecentlyViewedResourceRow = ({
  resource,
  org,
  horizontal = false,
}: {
  resource: { type: string; id: string };
  org: HomeDashOrg;
  horizontal?: boolean;
}) => {
  // Fetch resource data based on type
  const app = useSelector((s) =>
    resource.type === "app" ? selectAppById(s, { id: resource.id }) : undefined,
  );
  const database = useSelector((s) =>
    resource.type === "database"
      ? selectDatabaseById(s, { id: resource.id })
      : undefined,
  );
  const endpoint = useSelector((s) =>
    resource.type === "endpoint"
      ? selectEndpointById(s, { id: resource.id })
      : undefined,
  );
  const environment = useSelector((s) => {
    if (resource.type === "environment") {
      return selectEnvironmentById(s, { id: resource.id });
    }
    if (resource.type === "app" && app) {
      return selectEnvironmentById(s, { id: app.environmentId });
    }
    if (resource.type === "database" && database) {
      return selectEnvironmentById(s, { id: database.environmentId });
    }
    return undefined;
  });
  const stack = useSelector((s) => {
    if (resource.type === "stack") {
      const stack = selectStackById(s, { id: resource.id });
      return hasDeployStack(stack) ? stack : undefined;
    }
    // Get stack from environment for apps, databases, and environments
    if (environment && hasDeployEnvironment(environment)) {
      const stack = selectStackById(s, { id: environment.stackId });
      return hasDeployStack(stack) ? stack : undefined;
    }
    return undefined;
  });

  // Compute resource name, URL, and icon
  let resourceName = "";
  let resourceUrl = "";
  let iconPath = "";
  let iconAlt = "";

  switch (resource.type) {
    case "app":
      if (!app || !app.id) return null;
      resourceName = app.handle;
      resourceUrl = appDetailUrl(app.id);
      iconPath = "/resource-types/logo-app.png";
      iconAlt = "App";
      break;
    case "database":
      if (!database || !database.id) return null;
      resourceName = database.handle;
      resourceUrl = databaseDetailUrl(database.id);
      iconPath = database.type
        ? `/database-types/logo-${database.type}.png`
        : "/resource-types/logo-database.png";
      iconAlt = "Database";
      break;
    case "endpoint":
      if (!endpoint || !endpoint.id) return null;
      resourceName = getEndpointUrl(endpoint);
      resourceUrl = endpointDetailUrl(endpoint.id);
      iconPath = "/resource-types/logo-vhost.png";
      iconAlt = "Endpoint";
      break;
    case "environment":
      if (!environment || !environment.id) return null;
      resourceName = environment.handle;
      resourceUrl = environmentAppsUrl(environment.id);
      iconPath = "/resource-types/logo-environment.png";
      iconAlt = "Environment";
      break;
    case "stack":
      if (!stack || !stack.id) return null;
      resourceName = stack.name;
      resourceUrl = stackDetailEnvsUrl(stack.id);
      iconPath = getStackImg(getStackType(stack));
      iconAlt = "Stack";
      break;
    default:
      return null;
  }

  // Format ID display
  const idDisplay = `${capitalize(resource.type)} ID: ${resource.id}`;

  const iconSize = horizontal ? "w-6 h-6" : "w-8 h-8";
  const textSize = horizontal ? "text-sm" : "";
  const idTextSize = horizontal ? "text-xs" : "text-sm";
  const content = (
    <div className={`flex items-center ${horizontal ? "gap-1.5" : "gap-2"}`}>
      <img
        src={iconPath}
        className={`${iconSize} flex-shrink-0`}
        alt={iconAlt}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      <div
        className={`flex-1 min-w-0 overflow-hidden ${textSize} ${
          horizontal ? "" : "break-words"
        }`}
      >
        {resourceUrl ? (
          <Link
            to={resourceUrl}
            className={
              horizontal
                ? `${tokens.type["table link"]} block truncate`
                : `${tokens.type["table link"]} break-words`
            }
            onClick={() =>
              homeDashEvent(HOME_DASH_EVENTS.RECENTLY_VIEWED_RESOURCE_CLICK, {
                resourceType: resource.type,
                resourceId: resource.id,
                ...homeDashOrgPayload(org),
              })
            }
          >
            {resourceName}
          </Link>
        ) : (
          <div className={horizontal ? "truncate" : "break-words"}>
            {resourceName}
          </div>
        )}
        <div
          className={`text-black-300 ${idTextSize} ${
            horizontal ? "truncate" : "break-words"
          }`}
        >
          {idDisplay}
        </div>
      </div>
    </div>
  );

  if (horizontal) {
    return (
      <div className="flex-1 min-w-[140px] rounded-md border border-gray-200 bg-white px-3 py-2 shadow-sm">
        {content}
      </div>
    );
  }

  return (
    <Tr>
      <Td>{content}</Td>
    </Tr>
  );
};

export const RecentlyViewedResources = () => {
  const org = useSelector(selectOrganizationSelected);
  const resources = useSelector(selectResourcesByLastAccessedInCurrentOrg);
  const rows = resources.slice(0, 5);

  // Hide component if no resources found
  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col w-full">
      <h2 className="text-lg font-medium mb-1.5">Recently Viewed</h2>
      <div className="flex flex-row gap-2 w-full">
        {rows.map((resource) => (
          <RecentlyViewedResourceRow
            key={getResourceStatId(resource)}
            resource={resource}
            org={org}
            horizontal
          />
        ))}
      </div>
    </div>
  );
};
